/**
 * @fileoverview Call-to-action component with multiple display modes and responsive design.
 *
 * Renders donation call-to-action in three modes:
 * - Normal: Vertical layout with icon, title, description, and button
 * - Banner: Horizontal layout for full-width zones (icon left, text center, button right)
 * - Image: Custom image with optional overlay button
 *
 * Automatically adapts layout based on zone width and content type.
 *
 * @author Fundraiser Builder Team
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Heart } from "lucide-react";
import * as Icons from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { isLightColor, darkenColor } from "../utils/colorUtils";
import { IconType } from "../enums";
import { FundraiserCustomColorConfig } from "../models";

const CTAContainer = styled.div<{
  customColors?: FundraiserCustomColorConfig;
  isBanner?: boolean;
  isImageMode?: boolean;
}>`
  background: ${(props) => {
    if (props.isImageMode) {
      return "transparent";
    }
    return props.customColors?.accent?.rgba || "#E0E7FF";
  }};
  border-radius: ${(props) => (props.isImageMode ? "0" : "16px")};
  padding: ${(props) => (props.isImageMode ? "0" : "32px")};
  text-align: center;
  border: ${(props) => (props.isImageMode ? "none" : "1px solid #bbf7d0")};
  box-shadow: ${(props) =>
    props.isImageMode ? "none" : "0 8px 32px rgba(16, 185, 129, 0.1)"};
  display: flex;
  flex-direction: column;
  min-height: ${(props) => (props.isImageMode ? "auto" : "280px")};
  justify-content: center;
  position: relative;
  overflow: hidden;

  ${(props) =>
    props.isBanner &&
    !props.isImageMode &&
    `
    background: ${props.customColors?.accent.rgba || "linear-gradient(135deg, #f0fdf4, #ecfdf5)"};
    padding: 32px;
    border-radius: 24px;
    box-shadow: 0 8px 32px rgba(16, 185, 129, 0.1);
    min-height: 120px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  `}
`;

const CTAImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 16px;
  object-fit: cover;
  display: block;
`;
interface CTAImageButtonProps {
  isFullWidth?: boolean;
}

const CTAImageButton = styled.button<CTAImageButtonProps>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: ${(props) => (props.isFullWidth ? "18px 24px" : "12px 16px")};
  max-width: ${(props) => (props.isFullWidth ? "375px" : "250px")};
  border: none;
  border-radius: 12px;
  font-size: ${(props) => (props.isFullWidth ? "27px" : "18px")};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 2;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);

  &:hover {
    transform: translate(-50%, -50%) translateY(-2px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: translate(-50%, -50%) translateY(0);
  }
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
`;

const BannerTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`;

const BannerTitle = styled.h3<{ customColors?: FundraiserCustomColorConfig }>`
  font-size: 24px;
  font-weight: 800;
  margin: 0;
  color: ${(props) => props.customColors?.primary.rgba || "#059669"};
  line-height: 1.2;
`;

const BannerDescription = styled.p<{
  customColors?: FundraiserCustomColorConfig;
}>`
  font-size: 16px;
  margin: 0;
  line-height: 1.4;
  color: ${(props) => props.customColors?.primary.rgba || "#059669"};
`;

const BannerButton = styled.button`
  min-width: 200px;
  padding: 16px 24px;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
  &:disabled {
    background: #9ca3af !important;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const IconWrapper = styled.div<{ customColors?: FundraiserCustomColorConfig }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${(props) =>
    props.customColors?.primary.rgba
      ? props.customColors.primary.rgba + "80"
      : "#dcfce7"};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.2);
`;

const Title = styled.h3<{ customColors?: FundraiserCustomColorConfig }>`
  font-size: 20px;
  font-weight: 800;
  margin-bottom: 12px;
  color: ${(props) => props.customColors?.primary.rgba || "#059669"};
`;

const Description = styled.p<{ customColors?: FundraiserCustomColorConfig }>`
  font-size: 14px;
  margin-bottom: 24px;
  line-height: 1.5;
  color: ${(props) => props.customColors?.primary.rgba || "#059669"};
`;

const DonateButton = styled.button`
  min-width: 175px;
  max-width: 250px;
  padding: 12px 20px;
  margin: 0 auto;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

interface CallToActionProps {
  onDonateClick?: () => void;
  isPreview?: boolean;
  isBanner?: boolean;
  customColors?: FundraiserCustomColorConfig;
  buttonText?: string;
  showIcon?: boolean;
  ctaIconColor?: string;
  ctaIconBgColor?: string;
  showButtonOnImage?: boolean;
  isFullWidth?: boolean;
  isDisabled?: boolean;
}

export const CallToAction: React.FC<CallToActionProps> = observer(
  ({
    onDonateClick,
    isPreview = false,
    isBanner = false,
    customColors,
    buttonText = "Donate",
    showIcon = true,
    ctaIconColor,
    ctaIconBgColor,
    isDisabled,
    showButtonOnImage = true,
    isFullWidth = false,
  }) => {
    const colors = customColors || fundraiserStore.builderData?.customColors;
    const builderData = fundraiserStore.builderData || null;

    // Get CTA settings from builder data
    const ctaIconImageChoice = builderData?.ctaIconImageChoice || IconType.Icon;
    const ctaImage = builderData?.ctaImage;
    const ctaTitle = builderData?.ctaTitle || "Make a Difference Today";
    const ctaDescription =
      builderData?.ctaDescription ||
      "Your donation is critically important to achieving our fundraising goals.";
    const ctaIcon = builderData?.ctaIcon || "Heart";

    // Check if this is image mode (must have both image choice AND actual image)
    const isImageMode = ctaIconImageChoice === IconType.Image && !!ctaImage;

    // Use Design colors for icon: icon color = background, icon bg = secondary
    const iconColors = {
      bgColor:
        ctaIconBgColor || colors.secondary?.rgba || colors.secondary?.hex,
      iconColor:
        ctaIconColor || colors.background?.rgba || colors.background?.hex,
    };
    const handleDonateClick = () => {
      if (onDonateClick) {
        onDonateClick();
      }
    };

    const renderIcon = (iconName: string) => {
      const Icon = (Icons as any)[iconName];
      return Icon ? <Icon size={32} /> : <Heart size={32} />;
    };

    // Get button styles from store
    const getButtonStyles = () => {
      return fundraiserStore.getDonationButtonStyles();
    };

    // Image mode - show image with overlay button
    if (isImageMode) {
      return (
        <CTAContainer
          customColors={colors}
          isBanner={isBanner}
          isImageMode={true}
        >
          <CTAImage src={ctaImage.fullURL} alt="Call to Action" />
          {showButtonOnImage && (
            <CTAImageButton
              isFullWidth={isFullWidth}
              onClick={handleDonateClick}
              style={{
                ...getButtonStyles(),
                fontSize: isFullWidth ? "27px" : "18px",
              }}
              onMouseEnter={(e) => {
                const styles = getButtonStyles() as any;
                e.currentTarget.style.boxShadow = styles["--hover-shadow"];
              }}
              onMouseLeave={(e) => {
                const styles = getButtonStyles();
                e.currentTarget.style.boxShadow = styles.boxShadow as string;
              }}
            >
              {buttonText}
            </CTAImageButton>
          )}
        </CTAContainer>
      );
    }

    // Banner mode - show horizontal layout
    if (isBanner && !isImageMode) {
      return (
        <CTAContainer customColors={colors} isBanner={true} isImageMode={false}>
          <BannerContent>
            {showIcon && ctaIconImageChoice === IconType.Icon && (
              <IconWrapper
                customColors={colors}
                style={{
                  background: iconColors.bgColor,
                  color: iconColors.iconColor,
                  margin: 0,
                }}
              >
                <div style={{ color: iconColors.iconColor }}>
                  {renderIcon(ctaIcon)}
                </div>
              </IconWrapper>
            )}

            <BannerTextContent>
              <BannerTitle customColors={colors}>{ctaTitle}</BannerTitle>
              <BannerDescription customColors={colors}>
                {ctaDescription}
              </BannerDescription>
            </BannerTextContent>
          </BannerContent>

          <BannerButton
            onClick={handleDonateClick}
            style={{
              ...getButtonStyles(),
            }}
            onMouseEnter={(e) => {
              const styles = getButtonStyles() as any;
              e.currentTarget.style.boxShadow = styles["--hover-shadow"];
            }}
            onMouseLeave={(e) => {
              const styles = getButtonStyles();
              e.currentTarget.style.boxShadow = styles.boxShadow as string;
            }}
          >
            {buttonText}
          </BannerButton>
        </CTAContainer>
      );
    }

    // Normal mode with icon/text
    return (
      <CTAContainer customColors={colors} isBanner={isBanner}>
        {showIcon && ctaIconImageChoice === IconType.Icon && (
          <IconWrapper
            customColors={colors}
            style={{
              background: iconColors.bgColor,
              color: iconColors.iconColor,
            }}
          >
            <div style={{ color: iconColors.iconColor }}>
              {renderIcon(ctaIcon)}
            </div>
          </IconWrapper>
        )}

        <Title customColors={colors}>{ctaTitle}</Title>

        <Description customColors={colors}>{ctaDescription}</Description>

        <DonateButton
          disabled={isDisabled == true}
          onClick={handleDonateClick}
          style={{
            ...getButtonStyles(),
            fontSize: "18px",
          }}
          onMouseEnter={(e) => {
            const styles = getButtonStyles() as any;
            e.currentTarget.style.boxShadow = styles["--hover-shadow"];
          }}
          onMouseLeave={(e) => {
            const styles = getButtonStyles();
            e.currentTarget.style.boxShadow = styles.boxShadow as string;
          }}
        >
          {buttonText}
        </DonateButton>
      </CTAContainer>
    );
  }
);
